/**
 * Parses a number input's raw string, falling back (rather than coercing to
 * 0) when it is empty or not a number, and clamping to `[min, max]` when
 * given. Used so clearing a field mid-edit is a no-op, not a jump to 0/NaN
 * that would silently reach `updateStep` and, for params like halftone's
 * `lpi`/`dpi`, produce garbage downstream (division by zero) instead of a
 * validation error.
 */
export function clampNumber(raw: string, min: number | undefined, max: number | undefined, fallback: number): number {
  if (raw.trim() === '') return fallback;
  const n = Number(raw);
  if (isNaN(n)) return fallback;
  let v = n;
  if (min !== undefined && v < min) v = min;
  if (max !== undefined && v > max) v = max;
  return v;
}

/**
 * Resolves a field's local draft string into the value to commit: the same
 * fallback/clamp rules as `clampNumber`, plus rounding to a whole number
 * when the field steps by a whole number (so a fractional value typed into
 * a whole-number field, e.g. "1.5" into DPI, doesn't stick as a fraction).
 */
export function commitDraft(draft: string, current: number, min: number | undefined, max: number | undefined, step?: number): number {
  const v = clampNumber(draft, min, max, current);
  return step !== undefined && Number.isInteger(step) ? Math.round(v) : v;
}

/**
 * The next value for a draft-string state when the committed value it
 * mirrors changes externally: while the field is focused, an in-progress
 * edit is left alone (the commit path already reconciles it with the
 * committed value on blur/Enter/Escape); only once it's unfocused does the
 * draft adopt the fresh value. Without this, a value-sync `useEffect` keyed
 * on the committed value would stomp on whatever the user is mid-typing
 * every time something external (e.g. another field's run/debounce) makes
 * that value tick.
 */
export function nextDraft(prev: string, value: string, focused: boolean): string {
  return focused ? prev : value;
}

/**
 * A labelled slider with its number box. `hint` is the row's one-line
 * explanation in plain words, shown as a tooltip on the row AND on both inputs
 * — a `title` on the wrapping label alone never appears while the pointer is
 * over the control the user is actually dragging. It is never the accessible
 * name: `title` is only a fallback, and both inputs already have one (the
 * wrapping label names the range, `aria-label` names the number).
 */
export function Slider({ label, value, min, max, step = 1, unit = '', hint, onChange }: { label: string; value: number; min: number; max: number; step?: number; unit?: string; hint?: string; onChange: (v: number) => void }) {
  return (
    <label className="ctl" title={hint}>
      <span className="ctl-label">{label}</span>
      <input type="range" title={hint} min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      {/* The wrapping <label> can only name one control, and it names the range
          input; the number input needs its own name or it is announced unlabelled. */}
      <input type="number" className="ctl-num" title={hint} aria-label={label} min={min} max={max} step={step} value={value} onChange={(e) => onChange(clampNumber(e.target.value, min, max, value))} />
      {unit && <span className="muted small">{unit}</span>}
    </label>
  );
}
/**
 * A checkbox and the words it belongs to, in that order and next to each other.
 * NOT the `.ctl` grid the other controls use: that grid's 110-px first column
 * put the box ~105 px to the left of its own label, so the two read as
 * unrelated things. The label text stays the checkbox's accessible name.
 */
export function Toggle({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return <label className="toggle" title={hint}><input type="checkbox" title={hint} checked={checked} onChange={(e) => onChange(e.target.checked)} /><span>{label}</span></label>;
}
export function Select({ label, value, options, hint, onChange }: { label: string; value: string; options: { value: string; label: string }[]; hint?: string; onChange: (v: string) => void }) {
  return (
    <label className="ctl" title={hint}><span className="ctl-label">{label}</span>
      {/* aria-label as well as the wrapping <label>: it names the select
          explicitly (the wrapper's text is the same string), so a test or a
          screen reader can address "Vendor"/"Dot" rather than "the combobox". */}
      <select title={hint} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
    </label>
  );
}
